import {
  HRM_MANAGE_PERMISSIONS,
  SKILL_MANAGER_PERMISSIONS,
} from '@/constant/hr/manage-permissions';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { MenuItem, SidebarSection } from '@/types/sidebar.type';

/**
 * Grouped by migration phase (see HRM Angular -> React migration plan).
 * Each group grows as its phase's modules are built — add items here as
 * soon as a module's pages exist, not before.
 *
 * `name` must be a real UILM translation key that matches the key the
 * Angular app registers for the same nav item (see its
 * HrAndDirectoryComponent/IdentityAccessComponent `menuList` definitions) —
 * never human-readable text, so `t(item.name)` in SidebarMenuItemComponent
 * actually resolves to a translated label instead of falling back to the key.
 */
const hrDirectoryItems: MenuItem[] = [
  {
    id: 'hr-directory',
    name: 'DIRECTORY',
    path: '/hr/directory',
    icon: 'UsersThree',
    permissions: [HRM_PERMISSIONS.DIRECTORY.VIEW],
  },
];

const hrAttendanceItems: MenuItem[] = [
  {
    id: 'hr-my-attendance',
    name: 'MY_ATTENDANCE',
    path: '/hr/my-attendance',
    icon: 'Clock',
    permissions: [HRM_PERMISSIONS.ATTENDANCE.VIEW_OWN],
  },
  {
    id: 'hr-manage-attendance',
    name: 'MANAGE_ATTENDANCE',
    path: '/hr/manage-attendance',
    icon: 'ClipboardText',
    permissions: [HRM_PERMISSIONS.ATTENDANCE.VIEW_ALL],
  },
];

const hrLeaveItems: MenuItem[] = [
  {
    id: 'hr-my-leave',
    name: 'MY_LEAVE',
    path: '/hr/my-leave',
    icon: 'Airplane',
    permissions: [HRM_PERMISSIONS.LEAVE.VIEW_OWN],
  },
  {
    id: 'hr-leave-calendar',
    name: 'LEAVE_CALENDAR',
    path: '/hr/leave-calendar',
    icon: 'CalendarDots',
    permissions: [HRM_PERMISSIONS.LEAVE.VIEW_CALENDAR],
  },
  {
    id: 'hr-leave-manager',
    name: 'LEAVE_MANAGER',
    path: '/hr/leave-manager',
    icon: 'ClipboardText',
    permissions: [HRM_PERMISSIONS.LEAVE.VIEW_ALL],
  },
  {
    id: 'hr-sick-leave',
    name: 'SICK_LEAVE',
    path: '/hr/sick-leave',
    icon: 'FirstAidKit',
    permissions: [HRM_PERMISSIONS.SICK_LEAVE.VIEW],
  },
];

const hrToilItems: MenuItem[] = [
  {
    id: 'hr-my-toil',
    name: 'MY_TOIL',
    path: '/hr/my-toil',
    icon: 'Timer',
    permissions: [HRM_PERMISSIONS.TOIL.VIEW_OWN],
  },
  {
    id: 'hr-toil-manager',
    name: 'TOIL_MANAGER',
    path: '/hr/toil-manager',
    icon: 'ListChecks',
    permissions: [HRM_PERMISSIONS.TOIL.VIEW_ALL],
  },
];

const hrOvertimeItems: MenuItem[] = [
  {
    id: 'hr-overtime-manager',
    name: 'OVERTIME_MANAGER',
    path: '/hr/overtime-manager',
    icon: 'ClockCountdown',
    permissions: [HRM_PERMISSIONS.OVERTIME.VIEW],
  },
];

const hrTimesheetItems: MenuItem[] = [
  {
    id: 'hr-my-timesheet',
    name: 'MY_TIMESHEET',
    path: '/hr/my-timesheet',
    icon: 'Clock',
    permissions: [HRM_PERMISSIONS.TIMESHEET.VIEW_OWN],
  },
  {
    id: 'hr-timesheet-manager',
    name: 'TIMESHEET_MANAGER',
    path: '/hr/timesheet-manager',
    icon: 'Timer',
    permissions: [HRM_PERMISSIONS.TIMESHEET.VIEW_ALL],
  },
  {
    id: 'hr-mbm-project-manager',
    name: 'MBM_PROJECT_MANAGER',
    path: '/hr/mbm-project-manager',
    icon: 'Buildings',
    permissions: HRM_MANAGE_PERMISSIONS.MBM_PROJECT,
  },
];

const hrPerformanceItems: MenuItem[] = [
  {
    id: 'hr-performance-dashboard',
    // Under Performance Management already, so just "Dashboard".
    name: 'DASHBOARD',
    path: '/hr/performance-dashboard',
    icon: 'ChartLineUp',
    permissions: [HRM_PERMISSIONS.PERFORMANCE_DASHBOARD.VIEW],
  },
  {
    id: 'hr-my-submissions',
    name: 'MY_SUBMISSIONS',
    path: '/hr/my-submissions',
    icon: 'FileText',
    permissions: [HRM_PERMISSIONS.PERFORMANCE_SUBMISSION.VIEW_OWN],
  },
  {
    id: 'hr-reviews',
    name: 'REVIEWS',
    path: '/hr/reviews',
    icon: 'ClipboardText',
    permissions: [HRM_PERMISSIONS.PERFORMANCE_REVIEW.VIEW],
  },
  {
    id: 'hr-peer-requests',
    name: 'PEER_REQUESTS',
    path: '/hr/peer-requests',
    icon: 'UsersThree',
    permissions: [HRM_PERMISSIONS.PEER_REQUEST.VIEW],
  },
  {
    id: 'hr-form-creation',
    name: 'FORM_CREATION',
    path: '/hr/form-creation',
    icon: 'NotePencil',
    permissions: HRM_MANAGE_PERMISSIONS.PERFORMANCE_FORM,
  },
];

const hrLunchItems: MenuItem[] = [
  {
    id: 'hr-request-lunch',
    name: 'REQUEST_LUNCH',
    path: '/hr/request-lunch',
    icon: 'ForkKnife',
    permissions: [HRM_PERMISSIONS.LUNCH.VIEW_OWN],
  },
  {
    id: 'hr-manage-lunch',
    name: 'MANAGE_LUNCH',
    path: '/hr/manage-lunch',
    icon: 'ClipboardText',
    permissions: [HRM_PERMISSIONS.LUNCH.VIEW_ALL],
  },
];

const hrRecruitmentItems: MenuItem[] = [
  {
    id: 'hr-potential-profiles',
    name: 'POTENTIAL_PROFILES',
    path: '/hr/potential-profiles',
    icon: 'IdentificationCard',
    permissions: [HRM_PERMISSIONS.POTENTIAL_PROFILE.VIEW],
  },
  {
    id: 'hr-conditional-offers',
    name: 'CONDITIONAL_OFFERS',
    path: '/hr/conditional-offers',
    icon: 'Handshake',
    permissions: [HRM_PERMISSIONS.CONDITIONAL_OFFER.VIEW],
  },
];

const hrSurveyItems: MenuItem[] = [
  {
    id: 'hr-survey-my-submissions',
    name: 'MY_SURVEY_SUBMISSIONS',
    path: '/hr/survey-my-submissions',
    icon: 'ClipboardText',
    permissions: [HRM_PERMISSIONS.SURVEY_SUBMISSION.VIEW_OWN],
  },
  {
    id: 'hr-surveys',
    name: 'SURVEYS',
    path: '/hr/surveys',
    icon: 'ChartBar',
    permissions: [HRM_PERMISSIONS.SURVEY.VIEW],
  },
  {
    id: 'hr-survey-form-creation',
    name: 'SURVEY_FORM_CREATION',
    path: '/hr/survey-form-creation',
    icon: 'NotePencil',
    permissions: HRM_MANAGE_PERMISSIONS.SURVEY_FORM,
  },
];

const hrServiceDeskItems: MenuItem[] = [
  {
    id: 'hr-service-desk-noc',
    name: 'NOC_APPLICATION',
    path: '/hr/service-desk/noc',
    icon: 'Certificate',
    permissions: [
      HRM_PERMISSIONS.NOC_APPLICATION.VIEW,
      HRM_PERMISSIONS.NOC_APPLICATION.VIEW_REQUESTS,
    ],
  },
  {
    id: 'hr-service-desk-byod',
    name: 'BYOD_APPLICATION',
    path: '/hr/service-desk/byod',
    icon: 'Laptop',
    permissions: [
      HRM_PERMISSIONS.BYOD_APPLICATION.VIEW,
      HRM_PERMISSIONS.BYOD_APPLICATION.VIEW_REQUESTS,
    ],
  },
  {
    id: 'hr-service-desk-business-card',
    name: 'BUSINESS_CARD_APPLICATION',
    path: '/hr/service-desk/business-card',
    icon: 'Cardholder',
    permissions: [
      HRM_PERMISSIONS.BUSINESS_CARD_APPLICATION.VIEW,
      HRM_PERMISSIONS.BUSINESS_CARD_APPLICATION.VIEW_REQUESTS,
    ],
  },
  {
    id: 'hr-service-desk-id-card',
    name: 'ID_CARD_APPLICATION',
    path: '/hr/service-desk/id-card',
    icon: 'IdentificationCard',
    permissions: [
      HRM_PERMISSIONS.ID_CARD_APPLICATION.VIEW,
      HRM_PERMISSIONS.ID_CARD_APPLICATION.VIEW_REQUESTS,
    ],
  },
  {
    id: 'hr-service-desk-insurance',
    name: 'METLIFE_APPLICATION',
    path: '/hr/service-desk/insurance',
    icon: 'Umbrella',
    permissions: [
      HRM_PERMISSIONS.METLIFE_APPLICATION.VIEW,
      HRM_PERMISSIONS.METLIFE_APPLICATION.VIEW_REQUESTS,
    ],
  },
];

/**
 * Standalone top-level item, not a collapsible group -- mirrors the Angular
 * app's HrAndDirectoryComponent menuList, where 'Logs' is a single
 * `type: 'item'` entry (icon 'assignment') sitting alongside 'Directory' and
 * 'Project', not nested inside another section. `name` is 'Logs' (not the
 * usual ALL_CAPS_SNAKE convention) because that literal string is the actual
 * `translate` key the Angular app registers for this item -- see the same
 * component's menuList entry with id 'HRD' / url 'hrd/logs'.
 */
const hrLogsItems: MenuItem[] = [
  {
    id: 'hr-logs',
    name: 'Logs',
    path: '/logs',
    icon: 'ListBullets',
    permissions: [HRM_PERMISSIONS.LOGS.VIEW],
  },
];

const hrOrgStructureItems: MenuItem[] = [
  {
    id: 'hr-department-manager',
    name: 'DEPARTMENT_MANAGER',
    path: '/hr/department-manager',
    icon: 'Buildings',
    permissions: HRM_MANAGE_PERMISSIONS.DEPARTMENT,
  },
  {
    id: 'hr-role-manager',
    name: 'ROLE_MANAGER',
    path: '/hr/role-manager',
    icon: 'UserGear',
    permissions: HRM_MANAGE_PERMISSIONS.ROLE,
  },
  {
    id: 'hr-designation-manager',
    name: 'DESIGNATION_MANAGER',
    path: '/hr/designation-manager',
    icon: 'IdentificationBadge',
    permissions: HRM_MANAGE_PERMISSIONS.DESIGNATION,
  },
  {
    id: 'hr-location-manager',
    name: 'LOCATION_MANAGER',
    path: '/hr/location-manager',
    icon: 'MapPin',
    permissions: HRM_MANAGE_PERMISSIONS.LOCATION,
  },
  {
    id: 'hr-project-manager',
    name: 'PROJECT_MANAGEMENT',
    path: '/hr/project-manager',
    icon: 'Kanban',
    permissions: HRM_MANAGE_PERMISSIONS.PROJECT,
  },
  {
    id: 'hr-kpi-configuration',
    name: 'KPI_CONFIGURATION',
    path: '/hr/kpi-configuration',
    icon: 'Target',
    permissions: [HRM_PERMISSIONS.KPI.CONFIGURE],
  },
  {
    id: 'hr-skill-manager',
    name: 'SKILL_MANAGER',
    path: '/hr/skill-manager',
    icon: 'Lightbulb',
    permissions: SKILL_MANAGER_PERMISSIONS,
  },
  {
    id: 'hr-noc-type-manager',
    name: 'NOC_TYPE_MANAGER',
    path: '/hr/noc-type-manager',
    icon: 'FileText',
    permissions: HRM_MANAGE_PERMISSIONS.NOC_TYPE,
  },
  {
    id: 'hr-ctc-level-manager',
    name: 'CTC_LEVEL_MANAGER',
    path: '/hr/ctc-level-manager',
    icon: 'CurrencyCircleDollar',
    permissions: HRM_MANAGE_PERMISSIONS.CTC_LEVEL,
  },
  {
    id: 'hr-leave-builder',
    name: 'LEAVE_BUILDER',
    path: '/hr/leave-builder',
    icon: 'Wrench',
    permissions: HRM_MANAGE_PERMISSIONS.LEAVE_CATEGORY,
  },
  {
    id: 'hr-holiday',
    name: 'HOLIDAY',
    path: '/hr/holiday',
    icon: 'Confetti',
    permissions: HRM_MANAGE_PERMISSIONS.HOLIDAY,
  },
];

/**
 * The sidebar in display order. The sidebar renders these sections, and the route guard picks the
 * first permitted item across them as the landing page for a user who opens a URL they can't
 * access — so keep this order as the intended "home" priority.
 *
 * `icon` marks a section as a collapsible group (icon + label + chevron header that only
 * toggles, never navigates, with its items indented underneath) — this mirrors the Angular
 * app's `type: 'collapsable'` menuList entries (see HrAndDirectoryComponent). A section with no
 * `icon` (People, Activity) renders as a single flat link instead, mirroring Angular's
 * `type: 'item'` entries (Directory, Logs), which never had a group wrapper.
 */
export const hrSidebarSections: SidebarSection[] = [
  { title: 'PEOPLE', items: hrDirectoryItems },
  { title: 'ATTENDANCE', icon: 'Clock', items: hrAttendanceItems },
  { title: 'LEAVE_MANAGEMENT', icon: 'Airplane', items: hrLeaveItems },
  { title: 'TOIL_MANAGEMENT', icon: 'Timer', items: hrToilItems },
  { title: 'OVERTIME_MANAGEMENT', icon: 'ClockCountdown', items: hrOvertimeItems },
  { title: 'TIMESHEET_MANAGEMENT', icon: 'ChartNoAxesCombined', items: hrTimesheetItems },
  { title: 'PERFORMANCE_MANAGEMENT', icon: 'ChartLineUp', items: hrPerformanceItems },
  { title: 'LUNCH_SYSTEM', icon: 'ForkKnife', items: hrLunchItems },
  { title: 'PROFILES', icon: 'IdentificationCard', items: hrRecruitmentItems },
  { title: 'SURVEY', icon: 'ChartBar', items: hrSurveyItems },
  { title: 'SERVICE_DESK', icon: 'Headset', items: hrServiceDeskItems },
  { title: 'ORG_STRUCTURE', icon: 'Buildings', items: hrOrgStructureItems },
  { title: 'ACTIVITY', items: hrLogsItems },
];
